/* @flow */

import type { RouterHistory as History, Location } from "react-router";

import React from "react";
import { Route, Switch } from "react-router";
import Redirect from "@crossroads/react-router-redirect";
import { useData } from "crustate/react";
import {
  RouteData,
  OrderData,
  HomeData,
  BrandData,
  SearchData,
  PopularData,
  AllProductsData,
  CmsPageData,
  CmsBlockData,
} from "state/data";
import { PAGE_SIZE } from "effects/route";
import { StoreInfoContext } from "entrypoint/shared";
import { parseParams } from "helpers/location-search-string";
import { locationWithDefaults } from "helpers/hooks/use-update-product-list";
import { useUi } from "helpers/ui";
import Product, { HintProduct } from "pages/Product";
import ProductCard from "components/ProductCard";
import CategorySection from "components/CategorySection";
import { ProductListViewWrapper, ProductListView, HintProductListView } from "@crossroads/shop-views";
import NotFound from "pages/NotFound";
import CmsPage from "pages/CmsPage";
import Success from "pages/Success";
import Loading from "pages/Loading";
import Search from "pages/Search";
import Home from "pages/Home";
import Cart from "pages/Checkout/Cart";
import Overview from "pages/Checkout/Overview";
import { useTranslate } from "@awardit/react-use-translate";
import useShowPrices from "helpers/use-show-prices";

type RoutesProps = {
  location: Location,
};

type RouteViewProps = {
  location: Location,
  history: History,
  showMiniBanner: boolean,
};

const RouteView = ({ location, history, showMiniBanner }: RouteViewProps): React$Node => {
  const data = useData(RouteData);
  const { showPrices } = useShowPrices();

  if (data.state === "LOADING") {
    if (data.hint) {
      switch (data.hint.type) {
        case "product":
          return <HintProduct product={data.hint.product} />;
        case "category":
          return (
            <HintProductListView
              title={data.hint.category.name}
              StoreInfoContext={StoreInfoContext}
            />
          );
        case "cms_page":
          return <CmsPage cmsPage={data.hint.cmsPage} />;
        default:
      }
    }

    return <Loading />;
  }

  if (!data.route) {
    // TODO: Check error msg
    return <NotFound />;
  }

  switch (data.route.type) {
    case "product":
      return <Product product={data.route.product} />;
    case "category":
      return (
        <ProductListView
          hasFilters
          hasPagination
          filterbarOffsetClass={showMiniBanner ? "miniBannerOffset" : ""}
          variant="category"
          history={history}
          category={data.route.category}
          productList={data.route.category.products}
          title={data.route.category.name}
          isUpdating={data.state === "UPDATING"}
          pageSize={PAGE_SIZE}
          StoreInfoContext={StoreInfoContext}
          uiContext={useUi}
          ProductCardComponent={ProductCard}
          currentBreadcrumb={data.route.category.name}
          productsLink={location.pathname}
          priceLock={showPrices === false}
          afterContent={<CategorySection useWrapper />}
          CampaignCardData={CmsBlockData}
        />
      );
    case "cms_page":
      return <CmsPage cmsPage={data.route.cmsPage} />;
    case "redirect":
      return <Redirect to={data.route.url} />;
    default:
      return null;
  }
};

const Routes = ({ location }: RoutesProps): React$Node => {
  const t = useTranslate();
  const { showPrices } = useShowPrices();
  const miniBannerData = useData(CmsBlockData);
  const showMiniBanner = miniBannerData.state === "LOADED" &&
    miniBannerData.data !== null && miniBannerData.data.content !== "";

  return (
    <Switch>
      <Route
        exact path="/" render={() => (
          <HomeData.Provider>
            <CmsBlockData.Provider identifier="offerings">
              <Home />
            </CmsBlockData.Provider>
          </HomeData.Provider>
        )} />

      <Route
        path={t("URL.CHECKOUT_SUCCESS")}
        render={() => {
          return (
            <OrderData.Provider>
              <Success />
            </OrderData.Provider>
          );
        }} />

      <Route
        exact
        path={t("URL.CHECKOUT_OVERVIEW")}
        render={() => {
          return (
            <CmsPageData.Provider url={t("CHECKOUT.TERMS_URL")}>
              <Overview />
            </CmsPageData.Provider>
          );
        }}
      />

      <Route exact path={t("URL.CHECKOUT_CART")} component={Cart} />

      <Route
        path="/brand/:brand"
        render={({ match, location: { search } }) => {
          const { page } = parseParams(search);
          const brand = match.params.brand || "";

          return (
            <HomeData.Provider>
              <BrandData.Provider
                location={locationWithDefaults(location)}
                name={brand}
                brand={decodeURIComponent(brand)}
                page={page}
              >
                <CmsBlockData.Provider identifier="campaign-card">
                  <ProductListViewWrapper
                    filterbarOffsetClass={showMiniBanner ? "miniBannerOffset" : ""}
                    variant="brands"
                    data={BrandData}
                    pageSize={PAGE_SIZE}
                    StoreInfoContext={StoreInfoContext}
                    uiContext={useUi}
                    ProductCardComponent={ProductCard}
                    priceLock={showPrices === false}
                    CampaignCardData={CmsBlockData}
                    afterContent={<CategorySection useWrapper />}
                  />
                </CmsBlockData.Provider>
              </BrandData.Provider>
            </HomeData.Provider>
          );
        }} />

      <Route
        exact path="/popular-products"
        render={({ location: { search } }) => {
          const { page } = parseParams(search);
          return (
            <HomeData.Provider>
              <PopularData.Provider
                location={locationWithDefaults(location)}
                page={page}
              >
                <CmsBlockData.Provider identifier="campaign-card">
                  <ProductListViewWrapper
                    filterbarOffsetClass={showMiniBanner ? "miniBannerOffset" : ""}
                    variant="popular"
                    data={PopularData}
                    title={t("POPULAR.POPULAR_PRODUCTS")}
                    pageSize={PAGE_SIZE}
                    StoreInfoContext={StoreInfoContext}
                    uiContext={useUi}
                    productsLink="/popular-products"
                    ProductCardComponent={ProductCard}
                    priceLock={showPrices === false}
                    CampaignCardData={CmsBlockData}
                  />
                </CmsBlockData.Provider>
              </PopularData.Provider>
            </HomeData.Provider>
          );
        }} />

      <Route
        exact path="/all-products"
        render={({ location: { search } }) => {
          const { page } = parseParams(search);

          return (
            <HomeData.Provider>
              <AllProductsData.Provider
                location={locationWithDefaults(location)}
                page={page}
              >
                <CmsBlockData.Provider identifier="campaign-card">
                  <ProductListViewWrapper
                    filterbarOffsetClass={showMiniBanner ? "miniBannerOffset" : ""}
                    variant="allproducts"
                    data={AllProductsData}
                    title={t("ALL_PRODUCTS.TITLE")}
                    pageSize={PAGE_SIZE}
                    StoreInfoContext={StoreInfoContext}
                    uiContext={useUi}
                    productsLink="/all-products"
                    ProductCardComponent={ProductCard}
                    priceLock={showPrices === false}
                    CampaignCardData={CmsBlockData}
                    afterContent={<CategorySection useWrapper />}
                  />
                </CmsBlockData.Provider>
              </AllProductsData.Provider>
            </HomeData.Provider>
          );
        }} />

      <Route
        path="/search/:query"
        render={({ match, location: { search } }) => {
          const { page } = parseParams(search);

          const query = decodeURIComponent(match.params.query || "");

          return (
            <SearchData.Provider
              location={locationWithDefaults(location)}
              page={page}
              query={query}
              name={`search_${query}`}
            >
              <Search query={query} showMiniBanner={showMiniBanner} />
            </SearchData.Provider>
          );
        }}
      />

      <Route
        path="/(.+)"
        render={({ location: { pathname, search, state }, history }) => {
          const { page } = parseParams(search);

          return (
            <HomeData.Provider>
              <RouteData.Provider
                location={locationWithDefaults(location)}
                url={pathname}
                name={`route_${pathname}`}
                page={page}
                hint={state && state.hint}
              >
                <CmsBlockData.Provider identifier="campaign-card">
                  <RouteView
                    location={location}
                    history={history}
                    showMiniBanner={showMiniBanner}
                  />
                </CmsBlockData.Provider>
              </RouteData.Provider>
            </HomeData.Provider>
          );
        }} />
    </Switch>
  );
};

const AppRoutes = (): React$Node => <Route component={Routes} />;

export default AppRoutes;
